import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Clock,
  MapPin,
  Calendar,
  Trash2,
  AlertCircle,
  BookOpen,
  Award,
} from "lucide-react";
import { format } from "date-fns";
import { scheduleAPI, masterclassesAPI } from "../services/api";
import { useApp } from "../contexts/AppContext";
import "./MySchedule.css";

const MySchedule = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { showToast, currentEvent } = useApp();
  const [schedule, setSchedule] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [allMasterclasses, setAllMasterclasses] = useState([]); // Added to look up details
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(1);

  const days = useMemo(() => {
    if (!currentEvent) return [];
    
    const start = new Date(currentEvent.start_date);
    const end = new Date(currentEvent.end_date);
    
    // Normalize to midnight for calculation
    const sDate = new Date(start).setHours(0,0,0,0);
    const eDate = new Date(end).setHours(0,0,0,0);
    const diffDays = Math.round(Math.abs(eDate - sDate) / (1000 * 60 * 60 * 24)) + 1;
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return Array.from({ length: Math.max(1, diffDays) }, (_, i) => {
      const date = new Date(sDate);
      date.setDate(date.getDate() + i);
      
      return {
        id: i + 1,
        label: `Day ${i + 1}`,
        date: `${dayNames[date.getDay()]}, ${date.getDate()} ${monthNames[date.getMonth()]}`,
      };
    });
  }, [currentEvent]);

  useEffect(() => {
    fetchAllScheduleData();
  }, []);

  const fetchAllScheduleData = async () => {
    setLoading(true);
    try {
      // Fetch 1. Schedule, 2. My Registrations, 3. ALL Masterclasses (for details)
      const [scheduleRes, myRegRes, allMcRes] = await Promise.all([
        scheduleAPI.getMySchedule(),
        masterclassesAPI.getMyRegistrations(),
        masterclassesAPI.getAll(),
      ]);

      setSchedule(scheduleRes.data);
      setRegistrations(myRegRes.data);
      setAllMasterclasses(allMcRes.data);
    } catch (error) {
      console.error("Failed to fetch schedule data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      // Find the schedule item ID that corresponds to this session
      // Note: The list below uses "originalId" which is the schedule_item.id
      await scheduleAPI.removeFromSchedule(sessionId);
      setSchedule((prev) =>
        prev.filter(
          (s) => s.session_id !== sessionId && s.session?.id !== sessionId,
        ),
      );

      // Re-fetch to be safe or filter locally
      const updatedSchedule = schedule.filter(
        (item) => item.session.id !== sessionId,
      );
      setSchedule(updatedSchedule);

      showToast("Removed from schedule", "success");
    } catch (error) {
      showToast("Failed to remove", "error");
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "h:mm a");
    } catch {
      return "";
    }
  };

  const getSessionDay = (dateString) => {
    if (!dateString || !currentEvent) return 1;
    try {
      const sessionDate = new Date(dateString).setHours(0,0,0,0);
      const startDate = new Date(currentEvent.start_date).setHours(0,0,0,0);
      const diff = Math.round((sessionDate - startDate) / (1000 * 60 * 60 * 24));
      return diff + 1; // 1-indexed day
    } catch {
      return 1;
    }
  };

  // === MERGE & LOOKUP LOGIC ===

  // 1. Prepare Sessions
  const normalizedSessions = schedule.map((item) => ({
    id: item.session.id,
    uniqueId: `session-${item.session.id}`,
    title: item.session.title,
    type: item.session.session_type || "Session",
    start_time: item.session.start_time,
    end_time: item.session.end_time,
    location: item.session.location,
    isMasterclass: false,
    originalId: item.session.id,
  }));

  // 2. Prepare Masterclasses (with lookup)
  const normalizedMasterclasses = registrations.map((reg) => {
    // Attempt to find full details in the 'allMasterclasses' list using the ID
    const details =
      allMasterclasses.find((mc) => mc.id === reg.masterclass_id) || {};

    // Fallback to whatever data is in 'reg' if lookup fails
    return {
      id: reg.masterclass_id,
      uniqueId: `mc-${reg.masterclass_id}`,
      title: details.title || "Masterclass", // Use looked-up title
      type: "Masterclass",
      start_time: details.start_time, // Use looked-up time
      end_time: details.end_time,
      location: details.location || "TBA",
      isMasterclass: true,
      status: reg.status,
    };
  });

  // 3. Combine
  const allItems = [...normalizedSessions, ...normalizedMasterclasses];

  // 4. Filter by Day
  const filteredSchedule = allItems
    .filter(
      (item) => item.start_time && getSessionDay(item.start_time) === activeDay,
    )
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  const checkConflicts = () => {
    const conflicts = new Set();
    for (let i = 0; i < filteredSchedule.length - 1; i++) {
      const current = filteredSchedule[i];
      const next = filteredSchedule[i + 1];
      if (new Date(current.end_time) > new Date(next.start_time)) {
        conflicts.add(current.uniqueId);
        conflicts.add(next.uniqueId);
      }
    }
    return conflicts;
  };

  const conflictSet = checkConflicts();

  if (loading) {
    return (
      <div className="my-schedule-page">
      {days.length > 1 && (
        <div className="day-tabs">
          {days.map((day) => (
            <button
              key={day.id}
              className={`day-tab ${activeDay === day.id ? "active" : ""}`}
            >
              <span className="day-label">{day.label}</span>
              <span className="day-date">{day.date}</span>
            </button>
          ))}
        </div>
      )}
        <div className="schedule-timeline">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="schedule-item-skeleton">
              <div className="skeleton time-skeleton" />
              <div className="skeleton card-skeleton" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="my-schedule-page">
      {days.length > 1 && (
        <div className="day-tabs">
          {days.map((day) => (
            <button
              key={day.id}
              className={`day-tab ${activeDay === day.id ? "active" : ""}`}
              onClick={() => setActiveDay(day.id)}
            >
              <span className="day-label">{day.label}</span>
              <span className="day-date">{day.date}</span>
            </button>
          ))}
        </div>
      )}

      {conflictSet.size > 0 && (
        <div className="conflict-warning">
          <AlertCircle size={18} />
          <span>You have overlapping sessions</span>
        </div>
      )}

      {filteredSchedule.length > 0 ? (
        <div className="schedule-timeline">
          {filteredSchedule.map((item, index) => (
            <div
              key={item.uniqueId}
              className={`schedule-item ${conflictSet.has(item.uniqueId) ? "has-conflict" : ""}`}
            >
              <div className="timeline-marker">
                <div className="time-display">
                  {formatTime(item.start_time)}
                </div>
                <div className="timeline-dot" />
                {index < filteredSchedule.length - 1 && (
                  <div className="timeline-line" />
                )}
              </div>

              <div
                className="schedule-card"
                onClick={() => {
                  const prefix = eventId ? `/event/${eventId}` : "";
                  navigate(
                    item.isMasterclass
                      ? `${prefix}/masterclass/${item.id}`
                      : `${prefix}/session/${item.id}`,
                  );
                }}
              >
                <div className="schedule-card-header">
                  <span
                    className="session-type-badge"
                    style={{
                      backgroundColor: item.isMasterclass
                        ? "var(--rf-accent)"
                        : "var(--rf-primary)",
                    }}
                  >
                    {item.isMasterclass && item.status === "waitlist"
                      ? "Waitlist"
                      : item.type}
                  </span>

                  {!item.isMasterclass && (
                    <button
                      className="remove-btn"
                      onClick={(e) => handleRemoveSession(item.originalId, e)}
                      aria-label="Remove from schedule"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                  {item.isMasterclass && (
                    <Award size={16} style={{ color: "var(--rf-gray-400)" }} />
                  )}
                </div>

                <h3>{item.title}</h3>

                <div className="schedule-meta">
                  <div className="meta-item">
                    <Clock size={14} />
                    <span>
                      {formatTime(item.start_time)} -{" "}
                      {formatTime(item.end_time)}
                    </span>
                  </div>
                  <div className="meta-item">
                    <MapPin size={14} />
                    <span>{item.location}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">
            <BookOpen size={32} />
          </div>
          <h3>No sessions saved for this day</h3>
          <p>Browse the programme to add sessions</p>
          <button
            className="btn btn-primary"
            onClick={() =>
              navigate(eventId ? `/event/${eventId}/programme` : "/programme")
            }
          >
            <Calendar size={18} /> Browse Programme
          </button>
        </div>
      )}
    </div>
  );
};

export default MySchedule;
